import React, { useState, useEffect, useMemo } from "react";
import { Modal, Form, Dropdown, Button, ListGroup, Badge, Alert, FloatingLabel } from "react-bootstrap";
import axios from "axios";
import moment from "moment";
import { API_ENDPOINTS } from '../../config/api';
import ProgramPGYUtils from '../../utils/programPGYUtils';
import { useInstitutionalData } from '../../contexts/InstitutionalDataContext';
import ExpandableUserSelector from '../ExpandableUserSelector';

const eventTypeColors = {
  'Clinic': '#aec6e4', // Blue
  'OR': '#ffb3ba',     // Red
  'Meeting': '#b2e0b2', // Green
  'Education': '#ffdfba', // Yellow
  'Journal Club': '#dbffba', // lime 
  'Personal': '#c3b1e1', // Purple
  'Vacation': '#c7eff7', // Blue
  'Sick': '#e9f4b1', // Yellow
  'Weekend Call': '#fab6a3', // Red
  'Other': '#b2e7e0', // Gray
  'default': '#aec6e4'  // Default blue
};

export default function EditEventModal({ show, handleClose, event, onEventUpdated }) {
  const [formData, setFormData] = useState({
    title: '',
    start: '',
    end: '',
    location: '',
    label: '',
    description: '',
    allDay: false,
    repeat: 'none',
    repeatEnd: '',
  });

  const [attachments, setAttachments] = useState([]);
  const [newAttachments, setNewAttachments] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [existingInvitations, setExistingInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [programOptions, setProgramOptions] = useState([]);
  const [programMaxPGYById, setProgramMaxPGYById] = useState({});
  const [showUserDetails, setShowUserDetails] = useState(false);
  
  // PGY level configuration
  const [pgyLevelOptions, setPgyLevelOptions] = useState([]);
  const [maxPGYLevel, setMaxPGYLevel] = useState(5);
  const [programDetails, setProgramDetails] = useState(null);
  const [programLoading, setProgramLoading] = useState(false);

  // Institutional admins can invite users from any program in their institution.
  const isInstitutionalAdmin = typeof window !== 'undefined'
    && localStorage.getItem('role') === 'institutional_admin';
  const institutionalCache = useInstitutionalData();

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '',
            start: event.allDay ? moment.utc(event.start).local().format('YYYY-MM-DD') : moment.utc(event.start).local().format('YYYY-MM-DDTHH:mm'),
    end: event.allDay ? moment.utc(event.end).local().format('YYYY-MM-DD') : moment.utc(event.end).local().format('YYYY-MM-DDTHH:mm'),
        location: event.location || '',
        label: event.label || '',
        description: event.description || '',
        allDay: event.allDay || false,
        repeat: event.repeat || 'none',
        repeatEnd: event.repeatEnd ? moment(event.repeatEnd).format('YYYY-MM-DD') : '',
      });
      
      // Only fetch attachments and invitations for non-subscription events
      if (!event.source || event.source !== 'subscription') {
        fetchAttachments();
        fetchInvitations();
      } else {
        // For subscription events, clear any existing data
        setAttachments([]);
        setExistingInvitations([]);
      }
      fetchAvailableUsers();
    }
  }, [event]);

  useEffect(() => {
    let cancelled = false;
    if (!isInstitutionalAdmin || !programOptions?.length) {
      setProgramMaxPGYById({});
      return;
    }
    (async () => {
      const pairs = await Promise.all(
        programOptions.map(async (p) => {
          const mx = await ProgramPGYUtils.getMaxPGYLevel(p.id);
          const n = typeof mx === "number" && Number.isFinite(mx) && mx > 0 ? Math.floor(mx) : 5;
          return [p.id, n];
        })
      );
      if (!cancelled) {
        const next = {};
        pairs.forEach(([id, mx]) => {
          next[id] = mx;
        });
        setProgramMaxPGYById(next);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isInstitutionalAdmin, programOptions]);

  const selectableUsers = useMemo(() => (
    availableUsers.filter(
      (user) => !existingInvitations.some((inv) => inv.invitedUserId === user.id)
    )
  ), [availableUsers, existingInvitations]);

  // Fetch program details and PGY levels.
  // Institutional admins don't belong to a single program so we skip the
  // /my-program-details call (which 404s for them) and just fall back to the
  // default PGY levels.
  const fetchProgramDetails = async () => {
    if (isInstitutionalAdmin) {
      const fallbackOptions = Array.from({ length: 5 }, (_, i) => ({
        value: i + 1,
        label: `PGY-${i + 1}`,
      }));
      setPgyLevelOptions(fallbackOptions);
      setMaxPGYLevel(5);
      return;
    }

    setProgramLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await fetch(`${API_ENDPOINTS.PROGRAMS}/my-program-details`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        console.log('Program details received:', data);
        setProgramDetails(data);
        
        // Fetch PGY levels for this program
        if (data.id) {
          console.log('Fetching PGY levels for programId:', data.id);
          await fetchPGYLevels(data.id);
        } else {
          console.log('No programId found in program details');
        }
      }
    } catch (err) {
      console.error("Failed to fetch program details", err);
    } finally {
      setProgramLoading(false);
    }
  };

  // Fetch PGY level options for the program
  const fetchPGYLevels = async (programId) => {
    try {
      console.log('Fetching PGY levels for program:', programId);
      const options = await ProgramPGYUtils.getPGYLevelOptions(programId);
      console.log('Received PGY options:', options);
      setPgyLevelOptions(options);
      setMaxPGYLevel(options.length > 0 ? options[options.length - 1].value : 5);
    } catch (error) {
      console.error('Error fetching PGY levels:', error);
      // Fallback to default 5 levels
      const fallbackOptions = Array.from({ length: 5 }, (_, i) => ({
        value: i + 1,
        label: `PGY-${i + 1}`
      }));
      console.log('Using fallback PGY options:', fallbackOptions);
      setPgyLevelOptions(fallbackOptions);
      setMaxPGYLevel(5);
    }
  };

  const fetchAttachments = async () => {
    if (!event) return;
    
    // Skip fetching attachments for subscription events
    if (event.source === 'subscription') {
      setAttachments([]);
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_ENDPOINTS.EVENT_ATTACHMENTS}/event/${event.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttachments(response.data);
    } catch (error) {
      if (error?.response?.status === 404 || error?.response?.status === 403) {
        setAttachments([]); // Treat as no attachments available/authorized
        return;
      }
      console.error("Error fetching attachments:", error);
    }
  };

  const fetchInvitations = async () => {
    if (!event) return;
    
    // Skip fetching invitations for subscription events
    if (event.source === 'subscription') {
      setExistingInvitations([]);
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_ENDPOINTS.EVENT_INVITATIONS}/accessible-events/${event.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExistingInvitations(response.data);
    } catch (error) {
      if (error?.response?.status === 404 || error?.response?.status === 403) {
        setExistingInvitations([]); // No visible invitations for this user
        return;
      }
      console.error("Error fetching invitations:", error);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      if (isInstitutionalAdmin) {
        // Institutional admins get every user across every program in their
        // institution, served from the shared cache.
        const [users, programs] = await Promise.all([
          institutionalCache.fetchResource('users'),
          institutionalCache.fetchResource('programs'),
        ]);
        const list = Array.isArray(users) ? users : [];
        setAvailableUsers(list);
        const programList = Array.isArray(programs) ? programs : [];
        if (programList.length > 0) {
          setProgramOptions(programList.map((p) => ({ id: p.id, name: p.name })));
        } else {
          const byId = new Map();
          for (const u of list) {
            if (u.program && u.program.id && !byId.has(u.program.id)) {
              byId.set(u.program.id, { id: u.program.id, name: u.program.name });
            }
          }
          setProgramOptions(Array.from(byId.values()));
        }
      } else {
        const response = await axios.get(`${API_ENDPOINTS.USERS}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAvailableUsers(response.data);
      }

      // Also fetch program details and PGY levels
      await fetchProgramDetails();
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      // If allDay is checked, set start to beginning of day and end to 11:59 PM of end day
      if (name === "allDay") {
        if (checked) {
          newData.start = moment(newData.start).format("YYYY-MM-DD");
          newData.end = moment(newData.end).format("YYYY-MM-DD");
        } else {
          newData.start = moment.utc(newData.start).local().format("YYYY-MM-DDTHH:mm");
          newData.end = moment.utc(newData.end).local().format("YYYY-MM-DDTHH:mm");
        }
      }

      return newData;
    });
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setNewAttachments(prev => [...prev, ...files]);
    setSuccess("");
    setError("");
  };

  const handleRemoveNewAttachment = (index) => {
    setNewAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getCurrentUserId = () => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    
    try {
      // Decode JWT token to get user ID
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.id;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm("Are you sure you want to delete this attachment?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_ENDPOINTS.EVENT_ATTACHMENTS}/${attachmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAttachments(); // Refresh the list
      setSuccess("Attachment deleted successfully");
    } catch (error) {
      console.error("Error deleting attachment:", error);
      setError(error.response?.data?.error || "Failed to delete attachment");
    }
  };

  const handleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'accepted':
        return <Badge bg="success">Accepted</Badge>;
      case 'declined':
        return <Badge bg="danger">Declined</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No authentication token found");
        return;
      }

      // Format the data to match the API expectations
      const formattedData = {
        ...formData,
        start: formData.allDay 
          ? moment(formData.start).startOf('day').toISOString()
          : new Date(formData.start).toISOString(),
        end: formData.allDay 
          ? moment(formData.end).startOf('day').add(23, 'hours').add(59, 'minutes').toISOString()
          : new Date(formData.end).toISOString(),
        allDay: formData.allDay || false,
        repeat: formData.repeat || 'none',
        repeatEnd: formData.repeat !== "none" ? new Date(formData.repeatEnd).toISOString() : null,
      };

      const response = await axios.put(
        `${API_ENDPOINTS.EVENTS}/${event.id}`,
        formattedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Upload new attachments if any
      if (newAttachments.length > 0) {
        for (const file of newAttachments) {
          const attachmentFormData = new FormData();
          attachmentFormData.append('file', file);

          await axios.post(`${API_ENDPOINTS.EVENT_ATTACHMENTS}/upload/${event.id}`, attachmentFormData, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
        }
      }

      // Send new invitations only if any users are selected (best-effort)
      if (selectedUsers.length > 0) {
        try {
          console.log("Sending new invitations for users:", selectedUsers);
          await axios.post(`${API_ENDPOINTS.EVENT_INVITATIONS}/send/${event.id}`, {
            invitedUserIds: selectedUsers
          }, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        } catch (inviteErr) {
          console.warn("Invitation sending failed (non-blocking):", inviteErr?.response?.data || inviteErr?.message);
        }
      }

      // Show success message
      setSuccess("Event updated successfully!");
      
      // Update the UI with the new data
      if (response.data) {
        onEventUpdated(response.data);
      } else {
        onEventUpdated(event);
      }
      
      // Clear new attachments and selected users
      setNewAttachments([]);
      setSelectedUsers([]);
      
      // Close the modal after a short delay (no hard reload)
      setTimeout(() => {
        handleClose();
      }, 800);
      
    } catch (error) {
      console.error("Error updating event:", error);
      if (error.response) {
        setError(`Failed to update event: ${error.response.data.error || error.response.data.message || 'Unknown error'}`);
      } else {
        setError("Failed to update event. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="px-4 border-0 mt-2">
        <div style={{width: '50%'}}>
          <Modal.Title>Edit Event</Modal.Title>
          <div className="text-hl"></div>
        </div>
      </Modal.Header>
      <Modal.Body className="px-4">
        {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible>{success}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-lg-4 col-md-8">
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Title" className="mb-3">
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
                </FloatingLabel>
              </Form.Group>

              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Start Time" className="mb-3">
                <Form.Control
                  type={formData.allDay ? "date" : "datetime-local"}
                  name="start"
                  value={formData.start}
                  onChange={handleChange}
                  required
                />
                </FloatingLabel>
              </Form.Group>

              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="End Time" className="mb-3">
                <Form.Control
                  type={formData.allDay ? "date" : "datetime-local"}
                  name="end"
                  value={formData.end}
                  onChange={handleChange}
                  required
                />
                </FloatingLabel>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="allDay"
                  label="All Day Event"
                  checked={formData.allDay}
                  onChange={handleChange}
                />
              </Form.Group>
            </div>

            <div className="col-lg-3 col-md-4" >
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Repeat" className="mb-3">
                <Form.Select
                  name="repeat"
                  value={formData.repeat}
                  onChange={handleChange}
                >
                  <option value="none">No Repeat</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Biweekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </Form.Select>
                </FloatingLabel>
              </Form.Group>

              {formData.repeat !== "none" && (
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingInput" label="Repeat Until" className="mb-3">
                  <Form.Control
                    type="date"
                    name="repeatEnd"
                    value={formData.repeatEnd}
                    onChange={handleChange}
                    required
                  />
                  </FloatingLabel>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Location" className="mb-3">
                <Form.Control
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                />
                </FloatingLabel>
              </Form.Group>

              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingEditEventType" label="Event Type" className="mb-3">
                  <Dropdown className="w-100">
                    <Dropdown.Toggle
                      variant="light"
                      id="floatingEditEventType"
                      className="form-select text-start w-100 no-arrow d-flex align-items-center gap-2"
                      style={{ minHeight: '58px'}}
                    >
                      {formData.label ? (
                        <>
                          <span
                            style={{
                              display: 'inline-block',
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: eventTypeColors[formData.label] || eventTypeColors.default,
                              flexShrink: 0,
                            }}
                          />
                          <span className="text-truncate">{formData.label}</span>
                        </>
                      ) : (
                        <span className="text-muted">Select event type</span>
                      )}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100 no-arrow">
                      {Object.keys(eventTypeColors)
                        .filter((type) => type !== 'default')
                        .map((type) => (
                          <Dropdown.Item
                            key={type}
                            onClick={() => handleChange({ target: { name: 'label', value: type } })}
                            className="d-flex align-items-center gap-2"
                          >
                            <span
                              style={{
                                display: 'inline-block',
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: eventTypeColors[type],
                                flexShrink: 0,
                              }}
                            />
                            {type}
                          </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </FloatingLabel>
              </Form.Group>
            </div>
            <div className="col-lg-5 col-md-12 bg-light p-2 rounded-2 mb-3">
              <ExpandableUserSelector
                availableUsers={selectableUsers}
                selectedUsers={selectedUsers}
                onSelectionChange={setSelectedUsers}
                pgyLevelOptions={pgyLevelOptions}
                maxPGYLevel={maxPGYLevel}
                programMaxPGYById={isInstitutionalAdmin ? programMaxPGYById : undefined}
                isInstitutionalAdmin={isInstitutionalAdmin}
                programOptions={programOptions}
                helperText="Selected users will receive an invitation to event"
              />
            </div>
          </div>

          <Form.Group className="mb-3">
            <FloatingLabel controlId="floatingInput" label="Description" className="mb-3">
            <Form.Control
              as="textarea"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
            />
            </FloatingLabel>
          </Form.Group>

          {/* Attachments Section */}
          <Form.Group className="mb-3">
            <Form.Label>Attachments</Form.Label>
            <Form.Control
              type="file"
              multiple
              onChange={handleFileUpload}
              disabled={uploading}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp"
            />
            <Form.Text className="text-muted">
              Supported formats: PDF, Word, Excel, PowerPoint, Text, Images (max 10MB each)
            </Form.Text>
            {uploading && <div className="text-primary mt-1">Uploading...</div>}
          </Form.Group>

          {newAttachments.length > 0 && (
            <div className="mb-3">
              <Form.Label>New Files to Upload:</Form.Label>
              <div className="border rounded p-2">
                {newAttachments.map((file, index) => (
                  <div key={index} className="d-flex justify-content-between align-items-center mb-1">
                    <span className="text-truncate">{file.name}</span>
                    <div className="d-flex align-items-center gap-2">
                      <small className="text-muted">{formatFileSize(file.size)}</small>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveNewAttachment(index)}
                      >
                        <i className="bi bi-x"></i>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Existing Attachments Section */}
          {attachments.length > 0 && (
            <div className="mb-3">
              <Form.Label>Existing Attachments</Form.Label>
              <ListGroup>
                {attachments.map((attachment) => (
                  <ListGroup.Item key={attachment.id} className="d-flex justify-content-between align-items-center">
                    <div>
                      <i className="bi bi-paperclip me-2"></i>
                      {attachment.originalName}
                      <small className="text-muted ms-2">({formatFileSize(attachment.fileSize)})</small>
                      {attachment.uploader && (
                        <small className="text-muted ms-2">by {attachment.uploader.firstName} {attachment.uploader.lastName}</small>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          // Download logic
                          const token = localStorage.getItem("token");
                          axios.get(`${API_ENDPOINTS.EVENT_ATTACHMENTS}/download/${attachment.id}`, {
                            headers: { Authorization: `Bearer ${token}` },
                            responseType: 'blob'
                          }).then(response => {
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', attachment.originalName);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                          }).catch(error => {
                            console.error("Error downloading attachment:", error);
                            alert("Failed to download attachment");
                          });
                        }}
                      >
                        <i className="bi bi-download"></i>
                      </Button>
                      {attachment.uploadedBy === getCurrentUserId() && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteAttachment(attachment.id)}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      )}
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}



          {selectedUsers.length > 0 && (
            <div className="mb-3">
              <Form.Label>New Users to Invite:</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {selectedUsers.slice(0, 6).map(userId => {
                  const user = availableUsers.find(u => u.id === userId);
                  if (!user) return null;
                  return (
                    <span key={userId} className="badge rounded bg-secondary d-flex align-items-center gap-2 p-2" style={{ fontSize: '1em' }}>
                      <span className="fw-normal">{user.firstName} {user.lastName}</span>
                      <span className="small">({user.role})</span>
                      <Button
                        variant="danger"
                        size="sm"
                        className="px-1 py-0 ms-1"
                        onClick={() => handleUserSelection(userId)}
                        style={{ lineHeight: 1, fontSize: '1em', border: 'none', background: 'transparent', color: '#fff' }}
                        title="Remove user"
                      >
                        <i className="bi bi-x"></i>
                      </Button>
                    </span>
                  );
                })}
              </div>
              {selectedUsers.length > 6 && (
                <div>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none mt-2"
                    onClick={() => setShowUserDetails(!showUserDetails)}
                  >
                    <i className={`bi bi-chevron-${showUserDetails ? 'up' : 'down'} me-1`}></i>
                    {showUserDetails ? 'Hide' : `Show ${selectedUsers.length - 6} more users`}
                  </Button>
                  {showUserDetails && (
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {selectedUsers.slice(6).map(userId => {
                        const user = availableUsers.find(u => u.id === userId);
                        if (!user) return null;
                        return (
                          <span key={userId} className="badge rounded bg-secondary d-flex align-items-center gap-2 p-2" style={{ fontSize: '1em' }}>
                            <span className="fw-normal">{user.firstName} {user.lastName}</span>
                            <span className="small">({user.role})</span>
                            <Button
                              variant="danger"
                              size="sm"
                              className="px-1 py-0 ms-1"
                              onClick={() => handleUserSelection(userId)}
                              style={{ lineHeight: 1, fontSize: '1em', border: 'none', background: 'transparent', color: '#fff' }}
                              title="Remove user"
                            >
                              <i className="bi bi-x"></i>
                            </Button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Existing Invitations Section */}
          {existingInvitations.length > 0 && (
            <div className="mb-3">
              <Form.Label>Existing Invitations ({existingInvitations.length}):</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {existingInvitations.map((inv) => {
                  let badgeBg = 'secondary';
                  let icon = <i className="bi bi-clock"></i>;
                  if (inv.status === 'accepted') {
                    badgeBg = 'success';
                    icon = <i className="bi bi-check-circle-fill"></i>;
                  } else if (inv.status === 'declined') {
                    badgeBg = 'danger';
                    icon = <i className="bi bi-x-circle-fill"></i>;
                  }
                  const user = inv.invitedUser || {};
                  return (
                    <span key={inv.invitedUserId || (user.id || inv.id)} className={`badge rounded bg-${badgeBg} d-flex align-items-center gap-2 p-2`} style={{ fontSize: '1em' }}>
                      <span className="fw-normal">{user.firstName || 'N/A'} {user.lastName || ''}</span>
                      <span className="small">({user.role || 'N/A'})</span>
                      {icon}
                      {inv.isCreator && <span className="ms-2 badge rounded small py-1 px-2 bg-white text-muted fw-semibold">Creator</span>}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
} 